import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { articles } from '@/mocks/articles';
import { injectJsonLd } from './jsonld';

const SITE_NAME = 'CSSBuy Spreadsheet';
const DATE_MODIFIED = '2026-08-26';

const HOME_KEYWORDS =
  'CSSBuy Spreadsheet, CSSBuy Review, CSSBuy Shipping, CSSBuy Coupon, CSSBuy Guide, CSSBuy QC, compras internacionais, buying agent';
const ARTICLE_KEYWORDS =
  'CSSBuy Spreadsheet, CSSBuy Guide, CSSBuy QC, CSSBuy Shipping, CSSBuy Warehouse';

function normalizeLang(lang: string): 'pt' | 'de' | 'en' {
  const l = (lang || '').toLowerCase();
  if (l.startsWith('de')) return 'de';
  if (l.startsWith('en')) return 'en';
  return 'pt';
}

function getSiteUrl(): string {
  const envUrl = import.meta.env.VITE_SITE_URL as string | undefined;
  const base = envUrl || window.location.origin;
  return base.replace(/\/+$/, '');
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function HeadManager() {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const lang = normalizeLang(i18n.language);
    const path = location.pathname;
    const siteUrl = getSiteUrl();
    const canonicalUrl = siteUrl + (path === '/' ? '/' : path);

    let title: string;
    let description: string;
    let keywords: string;
    let ogType = 'website';
    let jsonType: 'home' | 'article' | 'article-detail' = 'home';
    let isIndexable = true;

    let core = path;
    if (lang === 'de') core = path.replace(/^\/de(?=\/|$)/, '') || '/';
    else if (lang === 'en') core = path.replace(/^\/en(?=\/|$)/, '') || '/';

    if (core === '/' || core === '') {
      title = t('hero.title');
      description = t('hero.subtitle');
      keywords = HOME_KEYWORDS;
      jsonType = 'home';
    } else if (core === '/cssbuy-spreadsheet') {
      title = t('article.title');
      description = t('article.metaDesc');
      keywords = ARTICLE_KEYWORDS;
      jsonType = 'article';
    } else if (core.startsWith('/article/')) {
      const slug = core.split('/')[2];
      const article = articles.find((a) => a.slug === slug);
      if (article) {
        title = article.title[lang];
        description = article.excerpt[lang];
        const kw = article.title[lang].split(':')[0].trim();
        keywords = `CSSBuy Spreadsheet, ${kw}`;
        ogType = 'article';
        jsonType = 'article-detail';
      } else {
        title = `404 | ${SITE_NAME}`;
        description = t('notFound.message');
        keywords = HOME_KEYWORDS;
        isIndexable = false;
      }
    } else {
      title = `404 | ${SITE_NAME}`;
      description = t('notFound.message');
      keywords = HOME_KEYWORDS;
      isIndexable = false;
    }

    document.title = title;
    setMeta('name', 'description', description);
    setMeta('name', 'keywords', keywords);
    setCanonical(canonicalUrl);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'robots', isIndexable ? 'index, follow' : 'noindex, follow');

    if (isIndexable) {
      injectJsonLd({
        type: jsonType,
        title,
        description,
        path: path === '/' ? '/' : path,
        lang,
        dateModified: DATE_MODIFIED,
      });
    }
  }, [location.pathname, i18n.language, t]);

  return null;
}